import React, { useState } from 'react'
import { Input, SelectPicker } from 'rsuite'
import { FormikInputs } from './interfaces'
import './PhoneInput.scss'

interface StandalonePhoneProps {
  value?: string
  onChange?: (value: string) => void
  error?: boolean
  success?: boolean
}

function PhoneInput<V>(
  props: Readonly<FormikInputs<V> & StandalonePhoneProps>
): React.ReactElement {
  const {
    formik,
    placeholder = '',
    isDisabled,
    name,
    value: standaloneValue,
    onChange: standaloneOnChange,
    error: hasError,
    success: hasSuccess,
    className: extraClass,
    ...rest
  } = props

  const [countryCode, setCountryCode] = useState('+254')
  const [isFocused, setIsFocused] = useState(false)

  const countryOptions = [
    { label: '🇰🇪 +254', value: '+254', flag: '🇰🇪' },
    { label: '🇺🇬 +256', value: '+256', flag: '🇺🇬' },
    { label: '🇹🇿 +255', value: '+255', flag: '🇹🇿' },
    { label: '🇷🇼 +250', value: '+250', flag: '🇷🇼' },
    { label: '🇮🇳 +91', value: '+91', flag: '🇮🇳' },
    { label: '🇺🇸 +1', value: '+1', flag: '🇺🇸' },
    { label: '🇬🇧 +44', value: '+44', flag: '🇬🇧' }
  ]

  const rawValue = formik
    ? (formik.values as Record<string, any>)[name] ?? ''
    : standaloneValue ?? ''

  // Format: 712 345 678 — only when not focused
  const formatPhone = (digits: string): string => {
    const d = digits.replaceAll(/\D/g, '')
    if (d.length <= 3) return d
    if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`
    return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)}`
  }

  // Show raw digits while editing, formatted when blurred
  const displayValue = isFocused ? rawValue : formatPhone(rawValue)

  const handleCountryChange = (value: string | null) => {
    setCountryCode(value || '+254')
  }

  const handleChange = (val: string) => {
    const numericValue = val
      .replaceAll(/\D/g, '')
      .replace(/^0+/, '')
      .slice(0, 9)
    if (standaloneOnChange) {
      standaloneOnChange(numericValue)
    } else {
      formik?.setFieldValue(name, numericValue)
      formik?.setFieldTouched(name, true, false)
    }
  }

  const stateClasses = [
    isFocused && 'phone-input--focused',
    hasError && 'phone-input--error',
    hasSuccess && 'phone-input--success',
    extraClass
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={`phone-input ${stateClasses}`} role="group">
      <SelectPicker
        data={countryOptions}
        searchable={false}
        cleanable={false}
        disabled
        value={countryCode}
        onChange={handleCountryChange}
        className="phone-input__country-picker"
        appearance="subtle"
        renderValue={(value, item: any) => {
          const flag = item?.flag || ''
          return (
            <span className="phone-input__country-value">
              {flag} {value}
            </span>
          )
        }}
      />
      <Input
        name={name}
        data-testid={name}
        type="tel"
        value={displayValue}
        disabled={isDisabled}
        placeholder={placeholder}
        className="phone-input__input"
        maxLength={9}
        onChange={handleChange}
        {...rest}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
    </div>
  )
}

export default PhoneInput
